import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const heroSrc = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

function Btn({ url, label, kind, purple, preview, onClick, testid }) {
  if (!label) return null;
  const cls = kind === "primary"
    ? `inline-flex items-center gap-2 font-bold text-sm px-5 py-3 rounded-lg transition-colors ${purple ? "bg-[#C9A45C] text-[#25352C] hover:bg-[#B8944F]" : "bg-[#236B45] text-white hover:bg-[#1B5537]"}`
    : `inline-flex items-center gap-2 font-bold text-sm px-5 py-[11px] rounded-lg border-2 transition-colors ${purple ? "border-white/80 text-white hover:bg-white hover:text-[#236B45]" : "border-[#236B45] text-[#236B45] hover:bg-[#236B45] hover:text-white"}`;
  const body = <>{label}{kind === "primary" && <ArrowRight size={16} />}</>;
  if (preview || !url) return <span className={cls} data-testid={testid}>{body}</span>;
  if (url.startsWith("/")) return <Link to={url} onClick={onClick} className={cls} data-testid={testid}>{body}</Link>;
  return <a href={url} onClick={onClick} className={cls} data-testid={testid} rel="noopener">{body}</a>;
}

function Panel({ c, mobile, preview, onClick }) {
  const purple = c.panel_style === "purple";
  return (
    <div className={`${purple ? "bg-[#236B45] text-white" : "bg-white text-[#25352C]"} flex flex-col justify-center min-h-0 overflow-hidden ${mobile ? "px-5 pt-5 pb-14" : "px-8 xl:px-12 pt-8 pb-16"}`} data-testid="hero-panel">
      {c.badge && <span className="self-start bg-[#C9A45C] text-[#25352C] text-[11px] font-extrabold tracking-widest px-2.5 py-1 rounded uppercase">{c.badge}</span>}
      {c.title && <h1 className={`${c.badge ? "mt-3" : ""} font-extrabold tracking-tight leading-[1.12] ${mobile ? "text-[26px]" : "text-3xl xl:text-[40px]"}`} data-testid="hero-title">{c.title}</h1>}
      {c.offer_text && <p className={`mt-3 font-bold border-l-[3px] border-[#C9A45C] pl-3 ${mobile ? "text-[15px]" : "text-base xl:text-lg"} ${purple ? "text-[#C9A45C]" : "text-[#236B45]"}`} data-testid="hero-offer">{c.offer_text}</p>}
      {c.description && <p className={`mt-3 leading-relaxed ${mobile ? "text-sm" : "text-[15px]"} ${purple ? "text-white/85" : "text-[#4A4A4A]"}`}>{c.description}</p>}
      {(c.primary_label || c.secondary_label) && (
        <div className="mt-5 flex flex-wrap gap-3">
          <Btn kind="primary" url={c.primary_url} label={c.primary_label} purple={purple} preview={preview} onClick={() => onClick?.("primary")} testid="hero-primary-btn" />
          <Btn kind="secondary" url={c.secondary_url} label={c.secondary_label} purple={purple} preview={preview} onClick={() => onClick?.("secondary")} testid="hero-secondary-btn" />
        </div>
      )}
    </div>
  );
}

function Img({ c, mobile, className = "" }) {
  const src = heroSrc((mobile && c.mobile_image) || c.image);
  const fx = mobile && c.mobile_image ? c.mobile_focal_x : c.focal_x, fy = mobile && c.mobile_image ? c.mobile_focal_y : c.focal_y;
  if (!src) return <div className={`bg-[#F0F7F2] flex items-center justify-center text-sm text-[#5F6E64] ${className}`}>No image yet</div>;
  return <img src={src} alt={c.alt || ""} className={`w-full h-full object-cover ${className}`} style={{ objectPosition: `${fx ?? 50}% ${fy ?? 50}%` }} draggable={false} data-testid="hero-image" />;
}

function Wrap({ c, preview, onClick, children, className }) {
  if (c.layout === "image_only" && c.primary_url && !preview) {
    const props = { className, onClick: () => onClick?.("banner"), "aria-label": c.alt || c.title || "Campaign", "data-testid": "hero-banner-link" };
    return c.primary_url.startsWith("/") ? <Link to={c.primary_url} {...props}>{children}</Link> : <a href={c.primary_url} {...props}>{children}</a>;
  }
  return <div className={className}>{children}</div>;
}

/** Renders one campaign. variant: "desktop" | "mobile" | "auto" (responsive). */
export default function HeroSlide({ c, variant = "auto", preview, onClick }) {
  const imageOnly = c.layout === "image_only";
  const desktop = (
    <Wrap c={c} preview={preview} onClick={onClick} className={`block h-full overflow-hidden ${imageOnly ? "relative" : "grid grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] grid-rows-[100%]"}`}>
      <div className="relative h-full min-h-0 overflow-hidden"><Img c={c} className="absolute inset-0" /></div>
      {!imageOnly && <Panel c={c} preview={preview} onClick={onClick} />}
    </Wrap>
  );
  const mobile = (
    <Wrap c={c} preview={preview} onClick={onClick} className="block">
      <div className={c.mobile_image ? "aspect-[4/5]" : "aspect-[16/11]"}><Img c={c} mobile /></div>
      {!imageOnly ? <Panel c={c} mobile preview={preview} onClick={onClick} /> : <div className="h-10 bg-white" />}
    </Wrap>
  );
  if (variant === "desktop") return <div className="h-[440px] bg-white">{desktop}</div>;
  if (variant === "mobile") return <div className="bg-white">{mobile}</div>;
  return <><div className="hidden md:block h-[400px] lg:h-[440px] bg-white">{desktop}</div><div className="md:hidden bg-white">{mobile}</div></>;
}
